import {
	Check,
	ChevronDown,
	ChevronRight,
	GripVertical,
	MoreVertical,
	Pencil,
	Trash2,
	X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { usePopoverMenu } from "../hooks/usePopoverMenu";

export const SwimlaneHeader = ({
	swimlane,
	itemCount,
	collapsed,
	onToggle,
	onRename,
	onDelete,
	onDragOver,
	onDrop,
	isDragTarget = false,
	onReorderDragStart,
	onReorderDragEnd,
}) => {
	const [showMenu, setShowMenu] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [title, setTitle] = useState(swimlane.title);
	const titleInputRef = useRef(null);
	const { triggerRef, menuRef, closeMenu } = usePopoverMenu(
		showMenu,
		setShowMenu,
	);

	useEffect(() => setTitle(swimlane.title), [swimlane.title]);
	useEffect(() => {
		if (isEditing) titleInputRef.current?.focus();
	}, [isEditing]);

	const cancelEditing = () => {
		setTitle(swimlane.title);
		setIsEditing(false);
	};

	const saveTitle = () => {
		const trimmedTitle = title.trim();
		if (trimmedTitle) onRename?.(swimlane.id, trimmedTitle);
		else setTitle(swimlane.title);
		setIsEditing(false);
	};

	return (
		<div
			className={`relative mb-2 flex min-h-10 items-center gap-2 rounded-md border bg-[var(--color-bg-primary)] px-3 py-2 shadow-sm transition-all ${
				isDragTarget
					? "border-[var(--color-accent)] ring-2 ring-[var(--color-accent)]"
					: "border-[var(--color-border)]"
			}`}
			onDragOver={onDragOver}
			onDrop={onDrop}
		>
			<button
				type="button"
				draggable
				onDragStart={(event) => onReorderDragStart(swimlane.id, event)}
				onDragEnd={onReorderDragEnd}
				className="cursor-grab rounded p-0.5 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] active:cursor-grabbing"
				aria-label={`Reorder ${swimlane.title} work stream`}
				title="Drag to reorder work stream"
			>
				<GripVertical className="h-4 w-4" />
			</button>
			{isEditing ? (
				<form
					className="flex items-center gap-1"
					onSubmit={(event) => {
						event.preventDefault();
						saveTitle();
					}}
				>
					<input
						ref={titleInputRef}
						value={title}
						onChange={(event) => setTitle(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === "Escape") cancelEditing();
						}}
						maxLength={80}
						className="w-64 rounded border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-2 py-1 text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
						aria-label="Swimlane name"
					/>
					<button
						type="submit"
						className="rounded p-1 hover:bg-[var(--color-bg-secondary)]"
						aria-label="Save swimlane name"
					>
						<Check className="h-4 w-4" />
					</button>
					<button
						type="button"
						onClick={cancelEditing}
						className="rounded p-1 hover:bg-[var(--color-bg-secondary)]"
						aria-label="Cancel renaming swimlane"
					>
						<X className="h-4 w-4" />
					</button>
				</form>
			) : (
				<button
					type="button"
					onClick={() => onToggle(swimlane.id)}
					className="flex min-w-0 items-center gap-2 rounded text-left hover:text-[var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
					aria-expanded={!collapsed}
				>
					{collapsed ? (
						<ChevronRight className="h-4 w-4 flex-none" />
					) : (
						<ChevronDown className="h-4 w-4 flex-none" />
					)}
					<span className="truncate font-semibold">{swimlane.title}</span>
					<span className="text-xs font-normal text-[var(--color-text-secondary)]">
						{itemCount}
					</span>
				</button>
			)}

			{swimlane.isCustom && !isEditing && (
				<div className="relative" ref={menuRef}>
					<button
						ref={triggerRef}
						type="button"
						onClick={() => setShowMenu((visible) => !visible)}
						className="rounded p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)]"
						aria-label={`Manage ${swimlane.title} swimlane`}
						aria-haspopup="menu"
						aria-expanded={showMenu}
					>
						<MoreVertical className="h-4 w-4" />
					</button>
					{showMenu && (
						<div
							role="menu"
							className="absolute left-0 mt-1 z-30 min-w-[170px] rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] py-1 shadow-lg"
						>
							<button
								type="button"
								role="menuitem"
								onClick={() => {
									closeMenu();
									setIsEditing(true);
								}}
								className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[var(--color-bg-secondary)]"
							>
								<Pencil className="h-4 w-4" /> Rename swimlane
							</button>
							<button
								type="button"
								role="menuitem"
								onClick={() => {
									closeMenu();
									onDelete?.(swimlane.id);
								}}
								className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-500 hover:bg-[var(--color-bg-secondary)]"
							>
								<Trash2 className="h-4 w-4" /> Delete swimlane
							</button>
						</div>
					)}
				</div>
			)}
		</div>
	);
};
